import { eur, eurCentimes } from "./explanations.js";

/**
 * La note de calcul d'un outil : chaque montant affiché, son calcul étape par
 * étape, puis les sources officielles et la date de leur vérification.
 *
 * ELLE NE CALCULE RIEN. Elle reprend les explications que l'outil affiche
 * déjà (lib/tools/explanations.js, contrôlées par npm run check:explanations)
 * et les met en page pour qu'on puisse les donner à un comptable, à une banque
 * ou à un proche. Ce qu'elle dit est donc exactement ce que l'écran disait.
 *
 * Elle rend un document au format de lib/tools/documentPdf.js
 * (`{ titre, blocs }`) : c'est ce module-là qui sait le poser sur une page.
 * Sans JSX, pour qu'un harnais puisse la fabriquer sans navigateur.
 */

const TEXTES = {
    fr: {
        titre: "Note de calcul : {outil}",
        intro: "Établie le {date} sur believemy.com, à partir des chiffres saisis dans l'outil «\u00A0{outil}\u00A0». Chaque montant est suivi de son calcul, étape par étape.",
        lien: "L'outil : {url}",
        etape: "Étape",
        calcul: "Calcul",
        resultat: "Résultat",
        sources: "D'où viennent ces chiffres",
        verifiee: "vérifiée le {date}",
        jours: "jours",
        trimestre: "trimestre",
        trimestres: "trimestres",
        abonne: "abonné",
        abonnes: "abonnés",
        avertissement: "À savoir",
    },
    en: {
        titre: "Calculation note: {outil}",
        intro: "Prepared on {date} on believemy.com, from the figures entered in the “{outil}” tool. Each amount is followed by its calculation, step by step.",
        lien: "The tool: {url}",
        etape: "Step",
        calcul: "Calculation",
        resultat: "Result",
        sources: "Where these figures come from",
        verifiee: "checked on {date}",
        jours: "days",
        trimestre: "quarter",
        trimestres: "quarters",
        abonne: "subscriber",
        abonnes: "subscribers",
        avertissement: "Please note",
    },
};

const remplir = (texte, valeurs) =>
    Object.entries(valeurs).reduce((t, [cle, v]) => t.split(`{${cle}}`).join(v), texte);

/**
 * Les polices standard d'un PDF ne connaissent pas tous les signes : l'espace
 * fine des milliers (« 40 000 € ») et le vrai signe moins sortiraient en
 * caractères cassés. On les ramène à leurs équivalents imprimables.
 *
 * L'espace qui suit « € » est doublée : l'aperçu de macOS dessine le signe
 * euro de la police standard plus large que sa chasse, et l'espace suivante
 * disparaissait dessous (« 42 000 €× 25,6 % »). Une insécable devant l'espace
 * ordinaire rend la respiration sans offrir de coupure de ligne de plus.
 */
export function textePdf(texte) {
    return String(texte ?? "")
        .replace(/[\u202F\u2009\u2007]/g, "\u00A0")
        .replace(/\u2212/g, "-")
        .replace(/\u2192/g, "->")
        .replace(/€ /g, "€\u00A0 ");
}

const dateLisible = (iso, lang) =>
    new Date(`${iso.slice(0, 10)}T12:00:00Z`).toLocaleDateString(lang === "en" ? "en-GB" : "fr-FR", {
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: "UTC",
    });

/** Le montant d'une étape, écrit comme dans le panneau de l'outil. */
function valeurEtape(e, lang, T) {
    if (e.unite === "jours") return `${e.valeur} ${T.jours}`;
    if (e.unite === "trimestres") {
        const pluriel = lang === "fr" ? e.valeur >= 2 : e.valeur !== 1;
        return `${e.valeur} ${pluriel ? T.trimestres : T.trimestre}`;
    }
    if (e.unite === "centimes") return eurCentimes(e.valeur, lang);
    if (e.unite === "abonnes") return `${e.valeur.toLocaleString(lang === "fr" ? "fr-FR" : "en-US")} ${e.valeur > 1 ? T.abonnes : T.abonne}`;
    return eur(e.valeur, lang);
}

/** Deux montants qui s'expliquent pareil ne font qu'une partie. */
function sansDoublons(explications) {
    const vues = new Set();
    return explications.filter((x) => {
        if (!x?.etapes?.length) return false;
        const empreinte = JSON.stringify(x.etapes.map((e) => [e.libelle, e.calcul, e.valeur]));
        if (vues.has(empreinte)) return false;
        vues.add(empreinte);
        return true;
    });
}

/**
 * @param {object} p
 * @param {string} p.lang
 * @param {string} p.nomOutil      le nom court de l'outil
 * @param {string} p.url           l'adresse de l'outil
 * @param {string} p.dateIso       la date du jour
 * @param {object[]} p.explications les explications affichées, dans l'ordre
 * @param {object} p.catalogue     les sources de l'outil, libellées ({ cle: { libelle, liens } })
 * @param {object} p.dates         la date de vérification de chaque source ({ cle: iso })
 * @param {string} p.avertissement la mention sous l'outil (indicatif, sans valeur juridique)
 */
export function documentNoteDeCalcul({ lang, nomOutil, url, dateIso, explications, catalogue, dates, avertissement }) {
    const L = lang === "en" ? "en" : "fr";
    const T = TEXTES[L];
    const parties = sansDoublons(explications);
    const blocs = [
        { type: "paragraphe", texte: remplir(T.intro, { date: dateLisible(dateIso, L), outil: nomOutil }) },
        { type: "note", texte: remplir(T.lien, { url }) },
    ];

    for (const x of parties) {
        const derniere = x.etapes[x.etapes.length - 1];
        const rangee = (e) => [e.libelle, e.calcul, valeurEtape(e, L, T)];
        blocs.push({ type: "intertitre", texte: derniere.libelle });
        // Une seule étape : son libellé est déjà l'intertitre, un tableau
        // d'une rangée le répéterait. Le calcul se lit en une ligne.
        if (x.etapes.length === 1) {
            const signe = derniere.operation?.type === "moteur" ? " : " : " = ";
            blocs.push({ type: "paragraphe", texte: `${derniere.calcul}${signe}${valeurEtape(derniere, L, T)}` });
            continue;
        }
        blocs.push({
            type: "tableau",
            entetes: [T.etape, T.calcul, T.resultat],
            largeurs: [56, 74, 30],
            alignements: ["gauche", "gauche", "droite"],
            lignes: x.etapes.slice(0, -1).map(rangee),
            total: rangee(derniere),
            aplat: true,
        });
    }

    const cles = [...new Set(parties.flatMap((x) => x.sources || []))].filter((cle) => catalogue?.[cle]);
    if (cles.length) {
        blocs.push({ type: "intertitre", texte: T.sources });
        for (const cle of cles) {
            const source = catalogue[cle];
            const verification = dates?.[cle] ? ` (${remplir(T.verifiee, { date: dateLisible(dates[cle], L) })})` : "";
            const liens = source.liens.map((l) => l.url).join(" ; ");
            blocs.push({ type: "note", texte: `${source.libelle}${verification} : ${liens}` });
        }
    }

    if (avertissement) {
        blocs.push({ type: "intertitre", texte: T.avertissement });
        blocs.push({ type: "note", texte: avertissement });
    }

    // Tout ce qui s'écrit passe par textePdf : les montants viennent de
    // toLocaleString, qui pose des espaces fines que la police ignore.
    const propre = (bloc) => ({
        ...bloc,
        ...(bloc.texte !== undefined ? { texte: textePdf(bloc.texte) } : {}),
        ...(bloc.entetes ? { entetes: bloc.entetes.map(textePdf) } : {}),
        ...(bloc.lignes ? { lignes: bloc.lignes.map((l) => l.map(textePdf)) } : {}),
        ...(bloc.total ? { total: bloc.total.map(textePdf) } : {}),
    });

    return {
        titre: textePdf(remplir(T.titre, { outil: nomOutil })),
        blocs: blocs.map(propre),
        parties: parties.length,
    };
}
